import { Injectable } from '@angular/core';
import { PlessHttp } from 'src/app/shared/http/pless-http.service';
import { PlessRequest } from 'src/app/shared/vo/pless-request.model';
import { PlessJsonResponderSub } from 'src/app/commons/kymw00/pless-json-responder-sub.service';
import { PlessDialogServiceSub } from 'src/app/commons/kymw00/pless-dialog-service-sub';
import {
  KYMW1001UserType,
  KYMW7002Model,
  MEDIUM_TYPE_STOCK_ONLY,
  KYMW7002PricePatternValue,
  APPROVAL_FLOW_TYPE_FEATURE_REPLACEMENT,
} from '../kymw7002.model';
import type {
  RegistSuggestionGoodsInfoCondition,
  RegistSuggestionGoodsInfoCombination
} from '../vo/regist-suggestion-goods-info-condition.model';

/** 処理ステータス */
export const enum ProcessStatus {
  /** 一時保存 */
  SAVE_TEMPORARY = '0',
  /** 提案 */
  SUGGEST = '1',
}

// リクエスト構造
export interface RegistSuggestionGoodsInfoRequest {
  regist: {
    SuggestionGoodsInfo: {
      condition: RegistSuggestionGoodsInfoCondition;
    };
    SuggestionGoodsList?: {
      combination: {
        parameter: RegistSuggestionGoodsInfoCombination;
      } [];
    };
  };
}

// レスポンス構造
export interface RegistSuggestionGoodsInfoResponse { }

/**
 * 提案商品情報 登録サービス
 * @export RegistSuggestionGoodsInfoService
 * @class RegistSuggestionGoodsInfoService
 * @extends {PlessJsonResponderSub}
 */
@Injectable()
export class RegistSuggestionGoodsInfoService extends PlessJsonResponderSub<RegistSuggestionGoodsInfoResponse> {
  model: KYMW7002Model;
  private requestData: RegistSuggestionGoodsInfoRequest;
  private calbak: () => void;

  constructor(
    protected plessHttp: PlessHttp,
    protected dialogService: PlessDialogServiceSub
  ) {
    super(plessHttp, dialogService);
  }

  /**
   * bodyにセットする値を返す
   *
   * @private
   * @returns {PlessRequest} リクエストJSON
   * @memberof RegistSuggestionGoodsInfoService
   */
  private getBody(status: ProcessStatus): PlessRequest {
    const condition: RegistSuggestionGoodsInfoCondition = {
      Status: status,
      OrderCode: this.model.orderCode,
      MediumType: this.model.mediumType,
      FeatureCode: this.model.featureCode,
      FeatureRequestId: this.model.selectedGoodsFlgResult.FeatureRequestId,
      // 選定担当区分
      SelectorType: (this.model.userType === KYMW1001UserType.WHOLESALE) ? 3
                  : (this.model.userType !== KYMW1001UserType.WHOLESALE && this.model.planningSupportFlg === '1') ? 1
                  : 0,
      User: this.model.userID,
    } as RegistSuggestionGoodsInfoCondition;

    // リクエストのベースを作成
    this.requestData = {
      regist: {
        SuggestionGoodsInfo: { condition }
      }
    };

    // リクエスト combination を作成
    const combinationList = [] as { parameter: RegistSuggestionGoodsInfoCombination }[];
    for (const goods of this.model.suggestionGoodsAllList) {
      // 今回仕入価格の設定
      let thisTimeCostPriceStandard = goods.ThisTimeCostPriceStandard;
      if (status === ProcessStatus.SUGGEST) {
        // ユーザ「企画支援」の場合は価格パターンに対応する値を設定
        if ((this.model.userType === KYMW1001UserType.PLANNING_SUPPORT)) {
          if (goods.PricePattern === KYMW7002PricePatternValue.SPECIAL3) {
            thisTimeCostPriceStandard = goods.CostPriceStandard;
          } else if (goods.PricePattern === KYMW7002PricePatternValue.STANDARD) {
            thisTimeCostPriceStandard = goods.CostPriceStandard;
          } else if (goods.PricePattern === KYMW7002PricePatternValue.SPECIAL1) {
            thisTimeCostPriceStandard = goods.CostPriceSpecial1;
          } else if (goods.PricePattern === KYMW7002PricePatternValue.SPECIAL2) {
            thisTimeCostPriceStandard = goods.CostPriceSpecial2;
          }
        }
      }
      // 計画供給高の設定（媒体区分が「在庫限定」の場合は在庫数、それ以外の場合は計画数から算出する）
      const planSupplyPrice = (this.model.mediumType === MEDIUM_TYPE_STOCK_ONLY)
                            ? goods.StockNum * goods.ThisTimeSupplyPriceStandard
                            : goods.PlanNum * goods.ThisTimeSupplyPriceStandard;

      const parameter: RegistSuggestionGoodsInfoCombination = {
        ReplacementStatus: (this.model.approvalFlowType === APPROVAL_FLOW_TYPE_FEATURE_REPLACEMENT) ? 1 : 0,
        PCode: goods.PCode,
        ItemCode: goods.ItemCode,
        StockNum: goods.StockNum,
        OrderUnitType: Number(goods.OrderUnitType),
        NewUnitType: Number(goods.NewUnitType),
        OrderNum: goods.OrderNum,
        PricePattern: Number(goods.PricePattern),
        ThisTimeSupplyPriceStandard: goods.ThisTimeSupplyPriceStandard,
        ThisTimeCostPriceStandard: thisTimeCostPriceStandard,
        PlanNum: goods.PlanNum,
        PlanSupplyPrice: planSupplyPrice,
        Remarks: goods.Remarks ?? '',
      }
      combinationList.push({ parameter });
    }
    // combination が存在すればセットする
    if (combinationList.length > 0) {
      this.requestData.regist.SuggestionGoodsList = {
        combination: combinationList
      }
    }

    return new PlessRequest(this.requestData);
  }

  /**
   * API呼び出し
   *
   * @param model
   * @memberof RegistSuggestionGoodsInfoService
   */
  execute(model: KYMW7002Model, status: ProcessStatus, f?: () => void): void {
    this.model = model;
    if (f) this.calbak = f;
    this.send('registSuggestionGoodsInfo', this.getBody(status));
  }

  /**
   * API通信成功時の処理
   *
   * @param {*} data レスポンスデータ
   * @memberof RegistSuggestionGoodsInfoService
   */
  success(data: RegistSuggestionGoodsInfoResponse) {
    if (this.calbak) this.calbak();
  }

}
